/**
 * KAN-837: блок "Когда можно звонить" в карточке менеджера кабинета.
 * Все видят часы строкой, владелец правит их тем же редактором, что у
 * менеджера в приложении (логика в useManagerWorkHours).
 */
import { Clock, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WorkHoursEditor } from '@/components/managers/WorkHoursEditor';
import { useManagerWorkHours } from '@/hooks/useManagerWorkHours';
import { formatWorkHours } from '@/lib/workHours';
import type { WorkHours } from '@/types/manager';

export const MANAGER_WORK_HOURS_TITLE = 'Когда можно звонить';

interface Props {
  managerId: string;
  workHours: WorkHours | undefined;
  canEdit: boolean;
}

export function ManagerWorkHoursBlock({ managerId, workHours, canEdit }: Props): JSX.Element {
  const { editing, draft, error, saving, start, cancel, change, save } = useManagerWorkHours(managerId, workHours);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4" data-testid="manager-work-hours">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Clock className="h-4 w-4 text-gray-400" aria-hidden="true" />
          {MANAGER_WORK_HOURS_TITLE}
        </h2>
        {canEdit && !editing && (
          <Button variant="ghost" size="sm" onClick={start} data-testid="manager-work-hours-edit">
            <Pencil className="mr-1 h-4 w-4" aria-hidden="true" />
            Изменить
          </Button>
        )}
      </div>
      {!editing && (
        <p className="mt-2 text-sm text-gray-700" data-testid="manager-work-hours-text">
          {formatWorkHours(workHours)}
        </p>
      )}
      {editing && (
        <div className="mt-3 max-w-md space-y-3">
          <WorkHoursEditor value={draft} onChange={change} error={error} disabled={saving} />
          <div className="flex gap-2">
            <Button onClick={() => void save()} disabled={saving} data-testid="manager-work-hours-save">
              Сохранить
            </Button>
            <Button variant="outline" onClick={cancel} disabled={saving}>
              Отмена
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
